
import {
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from "react";
import {
    ActivityIndicator,
    FlatList,
    Image,
    Modal,
    TouchableOpacity,
    View
} from "react-native";
import type {
    EventPhotoObjectType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    Text
} from "ncore-ui-kit";
import {
    useDebouncedDimensions
} from "../../../utils";
import {
    useNavigation,
    useRoute,
    type RouteProp
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    GetEventDetailPayloadType
} from "../../../services/restAPI/actions/getEventDetail/types";
import type AppStackParamList from "../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ChevronLeft,
    ChevronRight,
    X
} from "lucide-react-native";
import moment from "moment";
import {
    GestureHandlerRootView
} from "react-native-gesture-handler";
import Carousel from "react-native-snap-carousel";
import {
    ZoomableImage
} from "../../../components";
import {
    RESTService
} from "../../../services/restAPI";

const EventDetail = () => {
    const {
        windowWidth,
        actualWindowWidth
    } = useDebouncedDimensions();

    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();
    const route = useRoute<RouteProp<AppStackParamList, "EventDetail">>();

    const {
        eventTitle,
        eventID
    } = route.params;

    const {
        radiuses,
        borders,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [loading,
        setLoading] = useState(true);
    const [eventData,
        setEventData] = useState<GetEventDetailPayloadType>();
    const [eventYears,
        setEventYears] = useState<Array<string>>([]);
    const [eventPhotos,
        setEventPhotos] = useState<Array<EventPhotoObjectType>>([]);
    const [currentEventYear,
        setCurrentEventYear] = useState("");
    const [activeIndex,
        setActiveIndex] = useState(0);
    const [modalVisible,
        setModalVisible] = useState<boolean>(false);
    const [modalActiveIndex,
        setModalActiveIndex] = useState<number>(0);

    const inset = useSafeAreaInsets();

    const carouselRef = useRef(null);
    const carouselModalRef = useRef(null);

    useEffect(() => {
        getEventsData();
    }, []);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={{
                    paddingTop: inset.top,
                    paddingHorizontal: spaces.spacingMd,
                    backgroundColor: colors.content.container.default,
                    ...stylesheet.headerContainer
                }}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
                <View
                    style={{
                        ...stylesheet.headerContent,
                        top: inset.top + spaces.spacingSm
                    }}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                        numberOfLines={3}
                        style={stylesheet.headerTitle}
                    >
                        {eventTitle}
                    </Text>
                </View>
            </View>
        });
    }, [navigation]);

    const getEventsData = () => {
        RESTService.action("GetEventDetail", {
            eventID: eventID
        })
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    if(res.payload.pastEvents && res.payload.pastEvents.length) {
                        const eventYearPhotos = res.payload.pastEvents.reduce<Record<string, string[]>>((acc, currentEvent) => {
                            const year = moment(currentEvent.eventDate).format("YYYY");

                            if(!acc[year]) {
                                acc[year] = [];
                            }

                            if(currentEvent.photos && currentEvent.photos.length > 0) {
                                acc[year].push(...currentEvent.photos);
                            }

                            return acc;
                        }, {});

                        const sortedYears = Object.keys(eventYearPhotos).sort((a, b) => Number(b) - Number(a));

                        const mappedPhotosArray: Array<EventPhotoObjectType> = sortedYears.map((year) => {
                            return {
                                year: year,
                                photos: eventYearPhotos[year] || []
                            };
                        });
                        setCurrentEventYear(sortedYears[0] || "");
                        setEventYears(sortedYears);
                        setEventPhotos(mappedPhotosArray);
                    }

                    setEventData(res.payload);
                    setLoading(false);
                }
            }).catch((err) => {
                setLoading(false);
                console.log(err);
            });
    };

    const openGallery = (index: number) => {
        setModalActiveIndex(index);
        setModalVisible(true);
    };

    const renderCoverPhoto = () => {
        return<Image
            source={{
                uri: eventData?.coverPhoto
            }}
            style={{
                ...stylesheet.coverPhoto,
                borderRadius: radiuses.xl,
                marginBottom: spaces.spacingMd
            }}
        />;
    };

    const renderEventInfo = () => {
        return <View
            style={{
                paddingBottom: spaces.spacingXl,
                borderBottomWidth: borders.line,
                borderBottomColor: colors.content.border.subtle,
                marginBottom: spaces.spacingLg
            }}
        >
            <Text
                variant="headlineSmallSize"
                numberOfLines={3}
                color="high"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                {eventData?.eventTitle}
            </Text>
            <Text
                variant="bodyLargeSize"
            >
                {eventData?.eventDescription}
            </Text>
        </View>;
    };

    const renderEventPhotos = () => {
        if(!(eventYears && eventYears.length)) {
            return null;
        }
        const selectedYearPhotos = eventPhotos.find((item) => item.year === currentEventYear);

        const photosToRender = selectedYearPhotos ? selectedYearPhotos.photos : [];
        return <View>
            <Text
                variant="bodyLargeSize"
                style={{
                    marginBottom: spaces.spacingSm
                }}
            >
                {localize("eventImages")}
            </Text>
            <FlatList
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                keyExtractor={(item) => item}
                style={{
                    marginBottom: spaces.spacingLg
                }}
                renderItem={({
                    item,
                    index
                }) => {
                    return <View
                        style={{
                            marginRight: eventYears.length - 1  === index ? undefined : spaces.spacingMd,
                            borderRightWidth: eventYears.length - 1  === index ? undefined :  borders.line,
                            borderRightColor: eventYears.length - 1  === index ? null : colors.content.border.subtle,
                            paddingRight: eventYears.length - 1  === index ? undefined : spaces.spacingMd
                        }}
                    >
                        <TouchableOpacity
                            onPress={() => {
                                setCurrentEventYear(item);
                            }}
                            style={{
                                paddingHorizontal: spaces.spacingLg,
                                paddingVertical: spaces.spacingSm,
                                backgroundColor: currentEventYear === item ? colors.system.on.background: null,
                                borderRadius: radiuses.form
                            }}
                        >
                            <Text
                                variant="titleMediumSize"
                                color={currentEventYear === item ? "onPrimary" : "high"}
                            >
                                {item}
                            </Text>
                        </TouchableOpacity>
                    </View>;
                }}
                horizontal={true}
                data={eventYears}
            />
            <Carousel
                renderItem={({
                    item,
                    index
                }) => {
                    return <TouchableOpacity
                        disabled={index === activeIndex ? false :true}
                        onPress={() => {
                            openGallery(index);
                        }}
                    >
                        <Image
                            source={{
                                uri: item
                            }}
                            style={[
                                stylesheet.galleryImage,
                                {
                                    width: windowWidth * 0.75,
                                    borderRadius: radiuses.xl
                                }
                            ]}
                        />
                    </TouchableOpacity>;
                }}
                onSnapToItem={(index) => setActiveIndex(index)}
                itemWidth={windowWidth * 0.75}
                activeSlideAlignment="start"
                sliderWidth={windowWidth}
                inactiveSlideOpacity={1}
                inactiveSlideScale={1}
                data={photosToRender}
                layout={"default"}
                ref={carouselRef}
            />
        </View>;
    };

    const renderGalleryItem = ({
        item
    }: { item: string;
        index: number
    }) => {
        return <ZoomableImage
            uri={item}
        />;
    };

    const renderLightBox = () => {
        if(!(eventYears && eventYears.length)) {
            return null;
        }
        const selectedYearPhotos = eventPhotos.find((item) => item.year === currentEventYear);

        const photosToRender = selectedYearPhotos ? selectedYearPhotos.photos : [];
        return <Modal
            visible={modalVisible}
            transparent={true}
            animationType="fade"
            onRequestClose={() => setModalVisible(false)}
        >
            <View
                style={[
                    stylesheet.flex1,
                    stylesheet.modalOverlay,
                    {
                        backgroundColor: colors.content.container.overlay
                    }
                ]}
            >

                <TouchableOpacity
                    style={[
                        stylesheet.closeButton,
                        {
                            top: spaces.spacingLg + inset.top,
                            right: spaces.spacingLg,
                            padding: spaces.spacingMd,
                            backgroundColor: colors.content.icon.disabledWhite,
                            borderRadius: radiuses.full
                        }
                    ]}
                    onPress={() => setModalVisible(false)}
                >
                    <X/>
                </TouchableOpacity>

                <View
                    style={stylesheet.carouselContainer}
                >
                    <Carousel
                        ref={carouselModalRef}
                        data={photosToRender}
                        renderItem={renderGalleryItem}
                        sliderWidth={actualWindowWidth}
                        itemWidth={actualWindowWidth}
                        firstItem={modalActiveIndex}
                        onSnapToItem={(index: number) => setModalActiveIndex(index)}
                        inactiveSlideScale={1}
                    />

                    <TouchableOpacity
                        style={{
                            ...stylesheet.leftButton,
                            backgroundColor: colors.content.icon.disabledWhite,
                            borderRadius: radiuses.full,
                            left: spaces.spacingLg
                        }}
                        onPress={() => (carouselModalRef.current as unknown as { snapToPrev: () => void })?.snapToPrev()}
                    >
                        <ChevronLeft/>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={{
                            ...stylesheet.rightButton,
                            backgroundColor: colors.content.icon.disabledWhite,
                            borderRadius: radiuses.full,
                            right: spaces.spacingLg
                        }}
                        onPress={() => (carouselModalRef.current as unknown as { snapToNext: () => void })?.snapToNext()}
                    >
                        <ChevronRight/>
                    </TouchableOpacity>
                </View>

            </View>
        </Modal>;
    };

    if(loading) {
        return <PageContainer
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                size={40}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    return <GestureHandlerRootView>
        <PageContainer
            isScrollable={true}
            backgroundColor="subtle"
            scrollViewProps={{
                showsVerticalScrollIndicator: false
            }}
        >
            {renderCoverPhoto()}
            {renderEventInfo()}
            {renderEventPhotos()}
            {renderLightBox()}
        </PageContainer>
    </GestureHandlerRootView>;
};
export default EventDetail;
